import { SEOHead } from '../components/SEOHead';
import { ScrollReveal } from '../components/ScrollReveal';
import { ArrowUpRight, Mail, MapPin, Globe } from 'lucide-react';

const CALENDLY_URL = 'https://calendly.com/satyam-thriveitmedia/discovery-call';

export default function ContactPage() {
  return (
    <>
      <SEOHead
        title="Contact Thriveit Media | Book a Free Marketing Consultation"
        description="Get in touch with Thriveit Media — an influencer marketing and AI UGC agency. Book a free call to discuss your D2C brand's content and campaign needs."
        canonical="https://thriveitmedia.in/contact"
      />

      {/* Hero */}
      <section className="py-32 md:py-40 relative">
        <div className="absolute inset-0 grid-pattern" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <p className="text-xs uppercase tracking-[0.2em] font-heading font-bold text-[#FF5C35] mb-6">Contact</p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-none text-[#F5F5F0] max-w-4xl">
              Let's Talk About Growing Your Brand
            </h1>
          </ScrollReveal>
        </div>
      </section>

      {/* Contact Content */}
      <section data-testid="contact-section" className="py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left - Info */}
            <ScrollReveal>
              <div className="space-y-10">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#F5F5F0] mb-6">Book a Free 30-Minute Call</h2>
                  <p className="text-base font-body font-light text-[#888880] leading-relaxed mb-8">
                    Book a free 30-minute call with Satyam and we will give you an honest assessment of your current content and campaign setup — and tell you exactly what we would do differently. No commitment. No pitch deck. Just a practical conversation.
                  </p>
                  <a
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-cta-book-call"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-[#FFE500] text-[#0a0a0a] font-body font-medium tracking-wide hover:bg-[#FF5C35] transition-colors duration-300 group"
                  >
                    Book a Discovery Call
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </a>
                </div>

                <div className="space-y-6 pt-8 border-t border-white/5">
                  <div className="flex items-start gap-4">
                    <Mail className="w-5 h-5 text-[#FF5C35] mt-0.5" />
                    <div>
                      <p className="text-xs uppercase tracking-[0.15em] font-heading font-bold text-[#888880] mb-1">Email</p>
                      <a href="mailto:satyam@thriveitmedia.in" data-testid="contact-email" className="text-base font-body text-[#F5F5F0] hover:text-[#FF5C35] transition-colors">
                        satyam@thriveitmedia.in
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <MapPin className="w-5 h-5 text-[#FF5C35] mt-0.5" />
                    <div>
                      <p className="text-xs uppercase tracking-[0.15em] font-heading font-bold text-[#888880] mb-1">Location</p>
                      <p className="text-base font-body text-[#F5F5F0]">Bengaluru, Karnataka, India</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Globe className="w-5 h-5 text-[#FF5C35] mt-0.5" />
                    <div>
                      <p className="text-xs uppercase tracking-[0.15em] font-heading font-bold text-[#888880] mb-1">Website</p>
                      <p className="text-base font-body text-[#F5F5F0]">thriveitmedia.in</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <a
                    href="https://www.linkedin.com/in/satyamdokania/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-linkedin"
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-white/10 text-sm font-body text-[#F5F5F0] hover:border-[#FF5C35] hover:text-[#FF5C35] transition-colors"
                  >
                    LinkedIn <ArrowUpRight className="w-3.5 h-3.5" />
                  </a>
                  <a
                    href="https://www.instagram.com/aiagencyman/?hl=en"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-instagram"
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-white/10 text-sm font-body text-[#F5F5F0] hover:border-[#FF5C35] hover:text-[#FF5C35] transition-colors"
                  >
                    Instagram <ArrowUpRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            </ScrollReveal>

            {/* Right - Calendly Embed */}
            <ScrollReveal delay={0.15}>
              <div className="bg-[#111111] border border-white/5 p-8 md:p-12">
                <h3 className="font-heading text-xl font-bold text-[#F5F5F0] mb-2">Schedule a Time</h3>
                <p className="text-sm font-body font-light text-[#888880] mb-8">Pick a time that works for you. We typically respond within 24 hours.</p>
                <div className="bg-[#0a0a0a] border border-white/5 p-8 text-center">
                  <div className="space-y-6">
                    <div className="w-16 h-16 bg-[#FF5C35]/10 mx-auto flex items-center justify-center">
                      <svg className="w-8 h-8 text-[#FF5C35]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-lg font-heading font-bold text-[#F5F5F0] mb-2">Discovery Call</p>
                      <p className="text-sm font-body text-[#888880]">30 minutes | Free</p>
                    </div>
                    <a
                      href={CALENDLY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-calendly-embed-btn"
                      className="inline-flex items-center gap-2 px-8 py-4 bg-[#FFE500] text-[#0a0a0a] font-body font-medium tracking-wide hover:bg-[#FF5C35] transition-colors duration-300 group w-full justify-center"
                    >
                      Open Calendly
                      <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </a>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
      {/* What Happens After */}
      <section className="py-24 md:py-32 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-12">What Happens After You Book a Call</h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { num: '01', text: 'You book a 30-minute slot directly in our calendar via Calendly.' },
              { num: '02', text: 'We review your brand and Instagram presence before the call.' },
              { num: '03', text: 'On the call, we give you an honest assessment — what is working, what is not, and what we would do.' },
              { num: '04', text: 'If we are a good fit, we outline a proposal. If not, we tell you honestly and point you in the right direction.' },
            ].map((step) => (
              <ScrollReveal key={step.num} delay={parseInt(step.num) * 0.1}>
                <div className="bg-[#0a0a0a] border border-white/5 p-8">
                  <span className="text-4xl font-heading font-extrabold text-[#FFE500]/20">{step.num}</span>
                  <p className="text-sm font-body font-light text-[#888880] mt-4 leading-relaxed">{step.text}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
